import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconCalendarPlus, IconX } from '@tabler/icons-react';
import type { OnlaynZapisUvedomlenie } from './onlaynApi';
import { podklyuchitOnlayn, vzyatOnlaynZapis, type OnlaynSvyaz } from './onlaynVkladki';
import './onlayn-uvedomleniya.css';

interface KartochkaZapisi {
  zapis: OnlaynZapisUvedomlenie;
  istechet: number;
}

const SKRYT_CHEREZ_MS = 15_000;
const OTLOZHENNOE_ZHIVYOT_MS = 60_000;

/** Отдельное всплытие слева внизу; не модально и не отбирает фокус у работы админа. */
export function OnlaynZapisUvedomleniya({ userId }: { userId: number }) {
  const navigate = useNavigate();
  const [kartochki, setKartochki] = useState<KartochkaZapisi[]>([]);
  const svyaz = useRef<OnlaynSvyaz | null>(null);
  const pokazano = useRef(new Set<number>());
  const otlozhennye = useRef<OnlaynZapisUvedomlenie[]>([]);
  const naPauze = useRef(new Set<number>());
  const derzhim = useRef(new Map<number, () => void>());

  const pokazat = useCallback(async (zapisi: OnlaynZapisUvedomlenie[]) => {
    const svezhie = zapisi.filter((z) => !pokazano.current.has(z.id));
    if (svezhie.length === 0) return;
    if (document.visibilityState !== 'visible') {
      otlozhennye.current = [...otlozhennye.current, ...svezhie].slice(-30);
      return;
    }

    for (const zapis of svezhie) {
      if (pokazano.current.has(zapis.id)) continue;
      let otpustit: () => void = () => {};
      const derzhat = new Promise<void>((gotovo) => { otpustit = gotovo; });
      if (!(await vzyatOnlaynZapis(userId, zapis.id, derzhat))) { otpustit(); continue; }
      derzhim.current.set(zapis.id, otpustit);
      pokazano.current.add(zapis.id);
      svyaz.current?.skazat({ tip: 'pokazano', ids: [zapis.id] });
      setKartochki((bylo) => bylo.some((k) => k.zapis.id === zapis.id) ? bylo : [...bylo, { zapis, istechet: Date.now() + SKRYT_CHEREZ_MS }]);
    }
  }, [userId]);

  useEffect(() => {
    svyaz.current = podklyuchitOnlayn(userId, (p) => {
      if (p.tip === 'pokazano') {
        p.ids.forEach((id) => pokazano.current.add(id));
        otlozhennye.current = otlozhennye.current.filter((z) => !p.ids.includes(z.id));
        return;
      }
      void pokazat(p.zapisi);
    });

    const stalaVidimoy = () => {
      if (document.visibilityState !== 'visible' || otlozhennye.current.length === 0) return;
      const granica = Date.now() - OTLOZHENNOE_ZHIVYOT_MS;
      const svezhie = otlozhennye.current.filter((z) => z.kogda !== null && new Date(z.kogda).getTime() >= granica);
      otlozhennye.current = [];
      void pokazat(svezhie);
    };
    document.addEventListener('visibilitychange', stalaVidimoy);
    const chasy = window.setInterval(() => {
      const seychas = Date.now();
      setKartochki((bylo) => bylo.filter((k) => naPauze.current.has(k.zapis.id) || k.istechet > seychas));
      if (pokazano.current.size > 2000) pokazano.current = new Set([...pokazano.current].slice(-1000));
    }, 500);
    const zamki = derzhim.current;

    return () => {
      document.removeEventListener('visibilitychange', stalaVidimoy);
      window.clearInterval(chasy);
      svyaz.current?.ostanovit();
      svyaz.current = null;
      zamki.forEach((otpustit) => otpustit());
      zamki.clear();
    };
  }, [pokazat, userId]);

  useEffect(() => {
    const naEkrane = new Set(kartochki.map((k) => k.zapis.id));
    for (const [id, otpustit] of derzhim.current) {
      if (!naEkrane.has(id)) { otpustit(); derzhim.current.delete(id); }
    }
  }, [kartochki]);

  if (kartochki.length === 0) return null;

  const zakryt = (id: number) => {
    naPauze.current.delete(id);
    setKartochki((bylo) => bylo.filter((k) => k.zapis.id !== id));
  };

  return (
    <div className="onlayn-uved-stopka" role="region" aria-label="Новые онлайн-записи" aria-live="polite" data-testid="onlayn-uvedomleniya">
      {kartochki.map(({ zapis }) => (
        <section
          className="onlayn-uved-kartochka"
          key={zapis.id}
          data-testid="onlayn-uved-kartochka"
          data-zapis={zapis.id}
          onMouseEnter={() => naPauze.current.add(zapis.id)}
          onMouseLeave={() => {
            naPauze.current.delete(zapis.id);
            setKartochki((bylo) => bylo.map((k) => k.zapis.id === zapis.id ? { ...k, istechet: Date.now() + SKRYT_CHEREZ_MS } : k));
          }}
          onFocus={() => naPauze.current.add(zapis.id)}
          onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) naPauze.current.delete(zapis.id); }}
        >
          <div className="onlayn-uved-verh">
            <span className="onlayn-uved-ikonka"><IconCalendarPlus size={22} /></span>
            <b>Появилась новая онлайн-запись</b>
            <button type="button" className="onlayn-uved-krestik" onClick={() => zakryt(zapis.id)} aria-label="Скрыть уведомление" title="Скрыть уведомление">
              <IconX size={20} />
            </button>
          </div>
          <div className="onlayn-uved-svedeniya">
            {zapis.klientka !== null && <span>{zapis.klientka}</span>}
            <span>{[zapis.filial, zapis.vremya].filter(Boolean).join(' · ')}</span>
            {zapis.master !== null && <span>Мастер: {zapis.master}</span>}
            {zapis.usluga !== null && <span>{zapis.usluga}</span>}
          </div>
          <button type="button" className="onlayn-uved-otkryt" onClick={() => { zakryt(zapis.id); navigate(`/zapis/${zapis.id}`); }}>
            Открыть запись
          </button>
        </section>
      ))}
    </div>
  );
}
